import { action, makeAutoObservable } from 'mobx';
import { RuleApi } from '../../api/RuleApi';
import { Entity } from '../models/Entity';
import { Rule } from '../models/Rule';

export class EntitiesStore {
  entities: Map<number, Entity> = new Map();
  loadingRules: number[] = [];
  oldRules: Rule[] = [];
  ruleApptIdMap: Map<number, Rule> = new Map();
  ruleApptIdMapBuilt: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  addEntity = action((entity: Entity) => {
    this.entities.set(entity.id, entity);
  });

  get = (entityId: number): Entity => this.entities.get(entityId);

  getRule = (ruleId: number): Rule => {
    const rule = [...this.entities.values()]
      .map(e => e.rules)
      .reduce((a, b) => a.concat(b), [])
      .concat(this.oldRules)
      .filter(r => r.id === ruleId);
    
    return rule.length > 0 ? rule[0] : null;
  };

  buildRuleApptIdMap = () => {
    if (!this.ruleApptIdMapBuilt) {
      [...this.entities.values()]
        .flatMap(e => e.rules)
        .map(r => this.ruleApptIdMap.set(r.ruleApportionmentId, r));
      this.ruleApptIdMapBuilt = true;
    }
  };

  getRuleByRuleApportionmentId = (ruleApptId: number): Rule => {
    this.buildRuleApptIdMap();
    return this.ruleApptIdMap.get(ruleApptId) || null;
  };

  addRule = (rule: Rule) => {
    this.buildRuleApptIdMap();
    this.ruleApptIdMap.set(rule.ruleApportionmentId, rule);
    this.entities.get(rule.creatingEntity).addRule(rule);
  };

  loadRule = (groupId: number, ruleId: number): Promise<Rule> => {
    return RuleApi.getRule(groupId, ruleId)
      .then(response => {
        const rule = Rule.fromApi(response, groupId);
        rule.isLatest = false;
        this.oldRules.push(rule);
        this.ruleApptIdMap.set(rule.ruleApportionmentId, rule);

        return rule;
      });
  };

  loadRuleApportionmentId = (groupId: number, ruleApptId: number): Promise<Rule> => {
    return RuleApi.getRuleApportionment(groupId, ruleApptId)
      .then(response => {
        const rule = Rule.fromApi(response, groupId);
        rule.isLatest = false;
        this.oldRules.push(rule);
        this.ruleApptIdMap.set(rule.ruleApportionmentId, rule);

        return rule;
      });
  };

  nameFor = (entityId: number): string => {
    const entity = this.get(entityId);

    return entity ? entity.name : '';
  };

  get entitiesMap(): Map<number, string> {
    return new Map([...this.entities.keys()].map(entityId => ([entityId, this.nameFor(entityId)])));
  }

  allRulesForEntity = (entity: Entity): Rule[] => {
    // De-duplicate rules
    const rules = new Map(entity.rules.map(r => [r.ruleApportionmentId, r]));

    this.oldRules
      .filter(rule => rule.creatingEntity === entity.id)
      .forEach(rule => {
        if (!rules.has(rule.ruleApportionmentId)) rules.set(rule.ruleApportionmentId, rule);
      });

    return [...rules.values()];
  };
}
